import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  Divider,
  Alert,
  Tabs,
  Tab,
  Paper,
  Chip,
  LinearProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Container,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  AppBar,
  Toolbar,
  Drawer,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Snackbar
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  AddLink as AddLinkIcon,
  CloudUpload as CloudUploadIcon,
  Code as CodeIcon,
  Dashboard as DashboardIcon,
  SmartToy as SmartToyIcon,
  Storage as StorageIcon,
  Settings as SettingsIcon,
  Psychology as PsychologyIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Web as WebIcon,
  FileCopy as FileCopyIcon,
  Upgrade as UpgradeIcon,
  Language as LanguageIcon,
  ArrowForward as ArrowForwardIcon,
  Menu as MenuIcon,
  ExitToApp as LogoutIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Add as AddIcon,
  Article as ArticleIcon,
  AutoFixHigh as AutoFixHighIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  ContentCopy as ContentCopyIcon,
  Headset as HeadsetIcon // Add the HeadsetIcon import
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// Mini Robot Animation Component (reused from Dashboard)
const MiniRobot = ({ position }) => {
  const theme = useTheme();
  const [animationState, setAnimationState] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationState(prev => (prev + 1) % 3);
    }, 1500);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <Box
      sx={{
        position: 'absolute',
        ...position,
        width: 40,
        height: 40,
        zIndex: 0,
        transition: 'transform 0.5s ease-in-out',
        transform: `translateY(${animationState * 5}px) rotate(${animationState * 3}deg)`,
      }}
    >
      <svg viewBox="0 0 100 100" width="100%" height="100%">
        <rect x="30" y="20" width="40" height="40" rx="8" fill="#2C3E50" />
        <rect x="40" y="30" width="8" height="8" rx="2" fill={theme.palette.primary.main} />
        <rect x="52" y="30" width="8" height="8" rx="2" fill={theme.palette.primary.main} />
        <rect x="40" y="45" width="20" height="4" rx="2" fill={theme.palette.primary.main} />
        <rect x="20" y="35" width="10" height="5" rx="2" fill="#2C3E50" />
        <rect x="70" y="35" width="10" height="5" rx="2" fill="#2C3E50" />
        <rect x="35" y="60" width="10" height="15" rx="3" fill="#2C3E50" />
        <rect x="55" y="60" width="10" height="15" rx="3" fill="#2C3E50" />
      </svg>
    </Box>
  );
};

const MyBots = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  
  // States
  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [knowledgeFiles, setKnowledgeFiles] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [usage, setUsage] = useState({ tokensUsed: 0, requestsMade: 0 });
  const [embedStatus, setEmbedStatus] = useState({ deployed: false, deployedUrl: '' });
  const [embedCode, setEmbedCode] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // Add the missing state variable
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [websiteDialogOpen, setWebsiteDialogOpen] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [websiteError, setWebsiteError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deployUrlDialogOpen, setDeployUrlDialogOpen] = useState(false);
  const [deployUrl, setDeployUrl] = useState('');
  const [trainingBot, setTrainingBot] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [trainingFileId, setTrainingFileId] = useState(null); // Track which file is being trained
  const [trainingProgress, setTrainingProgress] = useState(0); // Track training progress
  const [trainingStatus, setTrainingStatus] = useState(''); // Track training status message
  const [trainingStep, setTrainingStep] = useState(0); // Add missing state for tracking steps
  const [trainingError, setTrainingError] = useState(''); // Add missing state for errors
  const [jsonlFileName, setJsonlFileName] = useState(''); // Add missing state for jsonl filename
  const [trainingModalOpen, setTrainingModalOpen] = useState(false);
  const [trainingState, setTrainingState] = useState('preparing'); // 'preparing', 'processing', 'completed', 'error'
  const [trainingDetails, setTrainingDetails] = useState({
    fileName: '',
    fileType: '',
    jsonlSections: 0
  });
  const [trainingAttempts, setTrainingAttempts] = useState(0);
  const [trainingErrorDetails, setTrainingErrorDetails] = useState('');
  const [retryTimeout, setRetryTimeout] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [selectedBotId, setSelectedBotId] = useState(null);

  const drawerWidth = 240;

  // Fetch bots
  const fetchBots = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBots(response.data);
      
      // If we have bots, select the first one by default
      if (response.data.length > 0) {
        setSelectedBot(response.data[0]);
        await fetchBotDetails(response.data[0].id);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching bots:', err);
      setError('Failed to load bots. Please try again later.');
      setLoading(false);
    }
  };

  // Fetch bot details (knowledge files, websites, usage, embed status)
  const fetchBotDetails = async (botId) => {
    try {
      // Fetch knowledge files
      const filesResponse = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${botId}/files`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setKnowledgeFiles(filesResponse.data);

      // Fetch websites
      const websitesResponse = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${botId}/websites`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWebsites(websitesResponse.data);

      // Fetch usage statistics
      const usageResponse = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${botId}/usage`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsage(usageResponse.data);

      // Fetch embed status
      const embedResponse = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${botId}/embed-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmbedStatus(embedResponse.data);

      // Generate embed code
      const scriptCode = `<script src="${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/embed/${botId}" async></script>
<div id="gorilla-forge-chatbot" data-bot-id="${botId}"></div>`;
      setEmbedCode(scriptCode);

    } catch (err) {
      console.error('Error fetching bot details:', err);
      setSnackbar({
        open: true,
        message: 'Error loading bot details',
        severity: 'error'
      });
    }
  };

  // Initial data load
  useEffect(() => {
    fetchBots();
  }, [token]);

  // Tab change handler
  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  // Select bot handler
  const handleSelectBot = async (bot) => {
    setSelectedBot(bot);
    await fetchBotDetails(bot.id);
    setTabValue(0); // Reset to first tab
  };

  // Refresh handler
  const handleRefresh = () => {
    if (selectedBot) {
      fetchBotDetails(selectedBot.id);
    } else {
      fetchBots();
    }
  };

  // Copy embed code handler
  const handleCopyEmbed = () => {
    navigator.clipboard.writeText(embedCode);
    setSnackbar({
      open: true,
      message: 'Embed code copied to clipboard!',
      severity: 'success'
    });
  };

  // Train bot handler
  const handleTrainBot = async (botId) => {
    try {
      setTrainingBot(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${botId}/knowledge/train`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSnackbar({
        open: true,
        message: 'Bot trained successfully!',
        severity: 'success'
      });
      
      // Refresh knowledge files and websites list
      fetchBotDetails(botId);
      
    } catch (err) {
      console.error('Error training bot:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to train bot',
        severity: 'error'
      });
    } finally {
      setTrainingBot(false);
    }
  };

  // Train a specific file
  const handleTrainFile = async (fileId, fileName, fileType) => {
    // Clear previous training states
    clearTimeout(retryTimeout);
    setTrainingFileId(fileId);
    setTrainingProgress(0);
    setTrainingStep(1);
    setTrainingStatus('Preparing training data...');
    setTrainingError('');
    setTrainingErrorDetails('');
    setTrainingAttempts(prev => prev + 1);
    setSelectedFileName(fileName || 'selected file');
  
    try {
      // Step 1: Translate the file to JSONL
      const prepareResponse = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${selectedBot.id}/knowledge/${fileId}/prepare-training`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000 // 30 second timeout for preparation
        }
      );
  
      // Store the JSONL file name for the next step
      const generatedJsonlFile = prepareResponse.data.jsonlFileName;
      setJsonlFileName(generatedJsonlFile);
      
      // Update progress to 50% after JSONL translation
      setTrainingProgress(50);
      setTrainingStep(2);
      setTrainingStatus(`JSONL file created successfully. Starting model fine-tuning...`);
  
      // Step 2: Fine-tune the model using the JSONL file
      try {
        const fineTuneResponse = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${selectedBot.id}/knowledge/${fileId}/fine-tune`,
          { jsonlFileName: generatedJsonlFile },
          { 
            headers: { Authorization: `Bearer ${token}` },
            timeout: 60000 // 60 second timeout for fine-tuning
          }
        );
  
        // Update progress to 100% when fine-tuning is complete
        setTrainingProgress(100);
        setTrainingStatus('Training complete! Your bot can now use this knowledge.');
  
        // Show success message
        setSnackbar({
          open: true,
          message: 'Bot successfully trained on the selected file!',
          severity: 'success'
        });
  
        // Refresh the file list to update status
        fetchBotDetails(selectedBot.id);
  
        // Clear training states after a delay to show completion
        setTimeout(() => {
          setTrainingFileId(null);
          setTrainingProgress(0);
          setTrainingStep(0);
          setTrainingStatus('');
          setJsonlFileName('');
          setTrainingAttempts(0);
        }, 3000);
      } catch (ftError) {
        handleTrainingError(ftError, fileId, fileName, 'fine-tuning', generatedJsonlFile);
      }
    } catch (error) {
      handleTrainingError(error, fileId, fileName, 'preparation');
    }
  };
  
  // New function to handle training errors with more detail
  const handleTrainingError = (error, fileId, fileName, stage, jsonlFileName = null) => {
    console.error(`Error during ${stage}:`, error);
    
    // Extract detailed error information
    const errorResponse = error.response?.data;
    const errorMessage = errorResponse?.message || error.message || `Failed to complete ${stage}`;
    const errorDetails = errorResponse?.error || error.toString();
    
    setTrainingError(errorMessage);
    setTrainingErrorDetails(errorDetails);
    
    // Show error in snackbar
    setSnackbar({
      open: true,
      message: `Training failed during ${stage}: ${errorMessage}`,
      severity: 'error'
    });
    
    // Allow retry if we haven't exceeded max attempts
    if (trainingAttempts < 3) {
      setTrainingStatus(`Training failed during ${stage}. Retrying in 5 seconds...`);
      
      // Auto-retry after 5 seconds
      const timeout = setTimeout(() => {
        // If we failed during fine-tuning, try to resume from there
        if (stage === 'fine-tuning' && jsonlFileName) {
          resumeTrainingFromFineTune(fileId, fileName, jsonlFileName);
        } else {
          handleTrainFile(fileId, fileName);
        }
      }, 5000);
      
      setRetryTimeout(timeout);
    } else {
      setTrainingStatus(`Training failed after multiple attempts. Please try again later.`);
      
      // Reset after showing the error
      setTimeout(() => {
        setTrainingFileId(null);
        setTrainingProgress(0);
        setTrainingStep(0);
        setTrainingStatus('');
        setTrainingAttempts(0);
      }, 5000);
    }
  };
  
  // New function to resume training from fine-tune step
  const resumeTrainingFromFineTune = async (fileId, fileName, jsonlFileName) => {
    setTrainingStep(2);
    setTrainingProgress(50);
    setTrainingStatus(`Resuming fine-tuning with previously prepared data...`);
    
    try {
      const fineTuneResponse = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${selectedBot.id}/knowledge/${fileId}/fine-tune`,
        { jsonlFileName },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000 
        }
      );
  
      // Update progress to 100% when fine-tuning is complete
      setTrainingProgress(100);
      setTrainingStatus('Training complete! Your bot can now use this knowledge.');
  
      // Show success message
      setSnackbar({
        open: true,
        message: 'Bot successfully trained on the selected file!',
        severity: 'success'
      });
  
      // Refresh the file list to update status
      fetchBotDetails(selectedBot.id);
  
      // Clear training states after a delay
      setTimeout(() => {
        setTrainingFileId(null);
        setTrainingProgress(0);
        setTrainingStep(0);
        setTrainingStatus('');
        setJsonlFileName('');
        setTrainingAttempts(0);
      }, 3000);
    } catch (error) {
      handleTrainingError(error, fileId, fileName, 'fine-tuning (retry)', jsonlFileName);
    }
  };

  // File upload handlers
  const handleUploadDialog = () => {
    setUploadDialogOpen(true);
    setSelectedFile(null);
    setFileError('');
    setUploadProgress(0);
  };
  
  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
  };
  
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setFileError('');
  };
  
  const handleFileUpload = async () => {
    if (!selectedBot) {
      setSnackbar({
        open: true,
        message: 'Please select a bot first',
        severity: 'error'
      });
      return;
    }
    
    if (!selectedFile) {
      setFileError('Please select a file to upload');
      return;
    }
    
    // Check file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setFileError('File size must be less than 10MB');
      return;
    }
    
    // Check file type
    const allowedTypes = [
      'application/pdf', 
      'text/plain', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/csv',
      'application/json'
    ];
    
    if (!allowedTypes.includes(selectedFile.type)) {
      setFileError('File type not supported. Please upload PDF, TXT, DOCX, CSV, or JSON files');
      return;
    }
    
    setUploadingFile(true);
    setUploadProgress(0);
    
    // Create form data
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      // Use the correct endpoint for file uploads
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${selectedBot.id}/knowledge/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      console.log("File upload response:", response.data);
      
      // Add the new file to the knowledge files list
      if (response.data) {
        // Refresh the file list to get the latest data
        fetchBotDetails(selectedBot.id);
      }
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'File uploaded successfully',
        severity: 'success'
      });
      
      // Close dialog
      setUploadDialogOpen(false);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      setFileError(error.response?.data?.message || 'Failed to upload file. Please try again.');
    } finally {
      setUploadingFile(false);
    }
  };

  // Delete file handler
  const handleDeleteFile = async (fileId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${selectedBot.id}/knowledge/${fileId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove file from list
      setKnowledgeFiles(prev => prev.filter(file => file.id !== fileId));
      
      setSnackbar({
        open: true,
        message: 'File deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting file:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete file',
        severity: 'error'
      });
    }
  };

  // Website dialog handlers
  const handleWebsiteDialog = () => {
    setWebsiteDialogOpen(true);
    setWebsiteUrl('');
    setWebsiteError('');
  };

  const handleCloseWebsiteDialog = () => {
    setWebsiteDialogOpen(false);
  };

  const handleWebsiteUrlChange = (e) => {
    setWebsiteUrl(e.target.value);
    setWebsiteError('');
  };

  const handleWebsiteAdd = async () => {
    // Basic URL validation
    if (!websiteUrl.trim()) {
      setWebsiteError('Please enter a website URL');
      return;
    }
    
    // Simple URL validation
    if (!websiteUrl.match(/^(http|https):\/\/[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i)) {
      setWebsiteError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${selectedBot.id}/knowledge/scrape`,
        { url: websiteUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add website to list
      setWebsites(prev => [...prev, response.data]);
      
      setSnackbar({
        open: true,
        message: 'Website added successfully',
        severity: 'success'
      });
      
      // Close dialog
      setWebsiteDialogOpen(false);
      
    } catch (error) {
      console.error('Error adding website:', error);
      setWebsiteError(error.response?.data?.message || 'Failed to add website. Please try again.');
    }
  };

  // Delete website handler
  const handleDeleteWebsite = async (websiteId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${selectedBot.id}/knowledge/website/${websiteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove website from list
      setWebsites(prev => prev.filter(website => website.id !== websiteId));
      
      setSnackbar({
        open: true,
        message: 'Website deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting website:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete website',
        severity: 'error'
      });
    }
  };

  // Deploy URL dialog handlers
  const handleDeployUrlDialog = () => {
    setDeployUrlDialogOpen(true);
    setDeployUrl(embedStatus.deployedUrl || '');
  };

  const handleCloseDeployUrlDialog = () => {
    setDeployUrlDialogOpen(false);
  };

  const handleDeployUrlChange = (e) => {
    setDeployUrl(e.target.value);
  };

  const handleSaveDeployUrl = async () => {
    // Simple URL validation
    if (deployUrl && !deployUrl.match(/^(http|https):\/\/[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid URL',
        severity: 'error'
      });
      return;
    }
    
    try {
      // In a real app, you would save this to the backend
      setEmbedStatus(prev => ({
        ...prev,
        deployed: true,
        deployedUrl: deployUrl
      }));
      
      setSnackbar({
        open: true,
        message: 'Deployment URL saved successfully',
        severity: 'success'
      });
      
      // Close dialog
      setDeployUrlDialogOpen(false);
      
    } catch (error) {
      console.error('Error saving deployment URL:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save deployment URL',
        severity: 'error'
      });
    }
  };

  // Drawer handlers
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Menu handlers
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Snackbar handler
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Status chip renderer
  const renderStatusChip = (status) => {
    switch (status) {
      case 'ready':
        return <Chip size="small" icon={<CheckIcon />} label="Ready" color="success" />;
      case 'processing':
        return <Chip size="small" icon={<PendingIcon />} label="Processing" color="warning" />;
      case 'failed':
        return <Chip size="small" icon={<ErrorIcon />} label="Failed" color="error" />;
      default:
        return <Chip size="small" label={status} />;
    }
  };

  // Training Animation Component
  const TrainingAnimation = ({ state, progress }) => {
    return (
      <Box sx={{ position: 'relative', height: 300, width: '100%', overflow: 'hidden' }}>
        {/* Background grid */}
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundImage: 'linear-gradient(rgba(66, 133, 244, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(66, 133, 244, 0.1) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          zIndex: 0
        }} />

        {/* PDF file animation */}
        <Box sx={{ 
          position: 'absolute',
          top: state === 'preparing' ? 50 : state === 'processing' ? 150 : 250,
          left: state === 'preparing' ? 40 : state === 'processing' ? 150 : 250,
          transition: 'all 1s ease-in-out',
          transform: state === 'completed' ? 'scale(0.5)' : 'scale(1)',
          zIndex: 2
        }}>
          <Box sx={{ 
            width: 70, 
            height: 80, 
            backgroundColor: '#f44336',
            borderRadius: '3px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            color: 'white',
            position: 'relative'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', opacity: 0.8 }}>PDF</Typography>
            <Box sx={{ 
              position: 'absolute', 
              top: -5, 
              right: -5, 
              width: 20, 
              height: 20, 
              borderRadius: '50%', 
              backgroundColor: state === 'preparing' ? '#FFC107' : state === 'processing' ? '#4CAF50' : '#1976D2',
              animation: state === 'preparing' ? 'pulse 1.5s infinite' : 'none',
              transition: 'background-color 0.5s ease'
            }} />
          </Box>
        </Box>

        {/* JSONL file animation */}
        <Box sx={{ 
          position: 'absolute',
          top: state === 'preparing' ? -50 : state === 'processing' ? 50 : 150,
          left: state === 'preparing' ? 150 : state === 'processing' ? 150 : 250,
          opacity: state === 'preparing' ? 0 : 1,
          transition: 'all 1s ease-in-out',
          transform: state === 'completed' ? 'scale(0.5)' : 'scale(1)',
          zIndex: 2
        }}>
          <Box sx={{ 
            width: 70, 
            height: 80, 
            backgroundColor: '#1976D2',
            borderRadius: '3px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            color: 'white',
            position: 'relative'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', opacity: 0.8 }}>JSONL</Typography>
            <Box sx={{ 
              position: 'absolute', 
              top: -5, 
              right: -5, 
              width: 20, 
              height: 20, 
              borderRadius: '50%', 
              backgroundColor: state === 'processing' ? '#FFC107' : state === 'completed' ? '#4CAF50' : state === 'error' ? '#f44336' : '#1976D2',
              animation: state === 'processing' ? 'pulse 1.5s infinite' : 'none',
              transition: 'background-color 0.5s ease'
            }} />
          </Box>
        </Box>

        {/* Robot/AI animation */}
        <Box sx={{ 
          position: 'absolute',
          top: 120,
          left: state === 'preparing' ? 280 : state === 'processing' ? 280 : 280,
          zIndex: 3
        }}>
          <Box sx={{ 
            width: 100, 
            height: 120, 
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative'
          }}>
            {/* Robot head */}
            <Box sx={{ 
              width: 70, 
              height: 70, 
              backgroundColor: '#4285F4',
              borderRadius: '15px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              position: 'relative'
            }}>
              {/* Robot eyes */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ 
                  width: 15, 
                  height: 15, 
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  animation: state !== 'error' ? 'blink 2s infinite' : 'redBlink 0.5s infinite'
                }} />
                <Box sx={{ 
                  width: 15, 
                  height: 15, 
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  animation: state !== 'error' ? 'blink 2s infinite 0.3s' : 'redBlink 0.5s infinite'
                }} />
              </Box>
              
              {/* Robot antenna */}
              <Box sx={{ 
                position: 'absolute', 
                top: -15, 
                width: 10, 
                height: 15, 
                backgroundColor: '#4285F4',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: -10,
                  left: 0,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: state === 'processing' ? '#FFC107' : state === 'completed' ? '#4CAF50' : state === 'error' ? '#f44336' : '#1976D2',
                  animation: state === 'processing' ? 'glow 1s infinite alternate' : 'none'
                }
              }} />
            </Box>
            
            {/* Robot body */}
            <Box sx={{ 
              width: 85, 
              height: 50, 
              backgroundColor: '#4285F4',
              borderRadius: '10px',
              marginTop: -1,
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              {/* Robot controls */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  backgroundColor: state === 'processing' ? '#FFC107' : '#ddd',
                  borderRadius: '50%',
                  animation: state === 'processing' ? 'glow 0.5s infinite alternate' : 'none'
                }} />
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  backgroundColor: state === 'processing' ? '#4CAF50' : '#ddd',
                  borderRadius: '50%',
                  animation: state === 'processing' ? 'glow 0.7s infinite alternate' : 'none'
                }} />
                <Box sx={{ 
                  width: 8, 
                  height: 8, 
                  backgroundColor: state === 'processing' ? '#f44336' : '#ddd',
                  borderRadius: '50%',
                  animation: state === 'processing' ? 'glow 0.9s infinite alternate' : 'none'
                }} />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Gear animation 1 */}
        <Box sx={{ 
          position: 'absolute',
          top: 80,
          left: 170,
          width: 40,
          height: 40,
          opacity: state === 'preparing' ? 0 : 1,
          transition: 'opacity 0.5s ease',
          animation: state === 'processing' ? 'spin 4s linear infinite' : 'none',
          zIndex: 1
        }}>
          <svg viewBox="0 0 24 24" width="100%" height="100%">
            <path fill="#9E9E9E" d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
            <path fill="#9E9E9E" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
            <path fill="#9E9E9E" d="M15.5 12c0 1.93-1.57 3.5-3.5 3.5S8.5 13.93 8.5 12 10.07 8.5 12 8.5s3.5 1.57 3.5 3.5zm4.5-2v4h-2.5c-.27 1.12-1.12 1.97-2.24 2.24v2.5h-4v-2.5c-1.12-.27-1.97-1.12-2.24-2.24H6v-4h2.5c.27-1.12 1.12-1.97 2.24-2.24V5h4v2.76c1.12.27 1.97 1.12 2.24 2.24H20z"/>
          </svg>
        </Box>

        {/* Gear animation 2 */}
        <Box sx={{ 
          position: 'absolute',
          top: 120,
          left: 210,
          width: 30,
          height: 30,
          opacity: state === 'preparing' ? 0 : 1,
          transition: 'opacity 0.5s ease',
          animation: state === 'processing' ? 'spin 4s linear infinite reverse' : 'none',
          zIndex: 1
        }}>
          <svg viewBox="0 0 24 24" width="100%" height="100%">
            <path fill="#607D8B" d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
            <path fill="#607D8B" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
            <path fill="#607D8B" d="M15.5 12c0 1.93-1.57 3.5-3.5 3.5S8.5 13.93 8.5 12 10.07 8.5 12 8.5s3.5 1.57 3.5 3.5zm4.5-2v4h-2.5c-.27 1.12-1.12 1.97-2.24 2.24v2.5h-4v-2.5c-1.12-.27-1.97-1.12-2.24-2.24H6v-4h2.5c.27-1.12 1.12-1.97 2.24-2.24V5h4v2.76c1.12.27 1.97 1.12 2.24 2.24H20z"/>
          </svg>
        </Box>

        {/* Binary code animation */}
        <Box sx={{ 
          position: 'absolute',
          bottom: 40,
          left: 0,
          right: 0,
          height: 30,
          display: 'flex',
          justifyContent: 'center',
          opacity: state !== 'preparing' ? 1 : 0,
          transition: 'opacity 0.5s ease',
        }}>
          <Typography variant="body2" sx={{ 
            fontFamily: 'monospace', 
            animation: state === 'processing' ? 'typewriter 3s infinite' : 'none',
            color: state === 'error' ? '#f44336' : '#1976D2',
          }}>
            {state === 'error' ? 'ERROR: 10101010 TRAINING FAILED' : 
             state === 'completed' ? 'SUCCESS: 10101010 TRAINING COMPLETE' :
             '01010100 10100101 01011010 10101010'}
          </Typography>
        </Box>

        {/* Status text */}
        <Box sx={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {state === 'preparing' ? 'Preparing training data...' : 
             state === 'processing' ? 'Training model...' :
             state === 'completed' ? 'Training complete!' :
             'Training failed!'}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Add function to handle copying public link
  const handleCopyPublicLink = (botId) => {
    setSelectedBotId(botId);
    const baseUrl = window.location.origin;
    const publicUrl = `${baseUrl}/public/${botId}`;
    navigator.clipboard.writeText(publicUrl);
    
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 3000);

    setSnackbar({
      open: true,
      message: 'Public link copied to clipboard!',
      severity: 'success'
    });
  };

  // Main content
  const renderMainContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 64px)">
          <CircularProgress size={60} />
        </Box>
      );
    }

    if (error) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 64px)">
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            {error}
          </Alert>
        </Box>
      );
    }

    if (bots.length === 0) {
      return (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          minHeight="calc(100vh - 64px)"
          padding={3}
        >
          <SmartToyIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 3 }} />
          <Typography variant="h5" gutterBottom align="center">
            You don't have any bots yet
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Create your first bot to start building your AI assistant
          </Typography>
          <Button 
            component={Link} 
            to="/bots/create" 
            variant="contained" 
            color="primary"
            startIcon={<SmartToyIcon />}
          >
            Create New Bot
          </Button>
        </Box>
      );
    }

    if (!selectedBot) {
      return (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          minHeight="calc(100vh - 64px)"
        >
          <Typography variant="h5" gutterBottom>
            Select a bot to manage
          </Typography>
        </Box>
      );
    }

    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Bot header */}
        <Box display="flex" alignItems="center" mb={3}>
          <SmartToyIcon fontSize="large" sx={{ mr: 2, color: selectedBot.customizations?.primaryColor || theme.palette.primary.main }} />
          <Typography variant="h4" component="h1">
            {selectedBot.name}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={handleChangeTab}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab icon={<SmartToyIcon />} label="Knowledge Base" />
            <Tab icon={<WebIcon />} label="Websites" />
            <Tab icon={<StorageIcon />} label="Usage & Stats" />
            <Tab icon={<CodeIcon />} label="Embed Code" />
          </Tabs>
        </Paper>

        {/* Knowledge Base Tab */}
        <Box hidden={tabValue !== 0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">Knowledge Files</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<UploadIcon />}
              onClick={handleUploadDialog}
            >
              Upload File
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Uploaded On</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {knowledgeFiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box py={3}>
                        <Typography variant="body1" color="text.secondary">
                          No knowledge files uploaded yet
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  knowledgeFiles.map((file) => (
                    <TableRow key={file.id}>
                      <TableCell>{file.name}</TableCell>
                      <TableCell>{file.type}</TableCell>
                      <TableCell>{(file.size / 1024).toFixed(1)} KB</TableCell>
                      <TableCell>{renderStatusChip(file.status)}</TableCell>
                      <TableCell>{new Date(file.uploadedAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<SmartToyIcon />}
                          onClick={() => handleTrainFile(file.id, file.originalName || file.name, file.type)}
                          disabled={trainingModalOpen}
                          sx={{ textTransform: 'none' }}
                        >
                          Train Bot
                        </Button>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDeleteFile(file.id)}>
                            <DeleteIcon color="error" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Training Progress UI */}
          {trainingFileId && (
            <Box 
              mt={4} 
              p={3} 
              borderRadius={2} 
              sx={{
                background: `linear-gradient(135deg, rgba(76, 133, 240, 0.08) 0%, rgba(96, 96, 246, 0.15) 100%)`,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(76, 133, 240, 0.2)',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  height: '100%', 
                  width: `${trainingProgress}%`,
                  background: 'rgba(76, 133, 240, 0.1)',
                  transition: 'width 0.5s ease-in-out'
                }} 
              />

              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" fontWeight="medium">
                    Training Progress
                  </Typography>
                  <Chip 
                    label={trainingStep === 1 ? 'JSONL Conversion' : 'Fine-Tuning'} 
                    color={trainingStep === 1 ? 'primary' : 'secondary'} 
                    size="small"
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {trainingStatus}
                </Typography>
                
                {trainingError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography fontWeight="medium">{trainingError}</Typography>
                    {trainingErrorDetails && (
                      <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                        Details: {trainingErrorDetails}
                      </Typography>
                    )}
                    {trainingAttempts < 3 && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Retry attempt {trainingAttempts}/3
                      </Typography>
                    )}
                  </Alert>
                )}
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={trainingProgress} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: 'rgba(76, 133, 240, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: trainingError ? 'rgba(244, 67, 54, 0.8)' : 'rgba(76, 133, 240, 0.8)',
                          backgroundImage: trainingError ? 'none' : 'linear-gradient(90deg, rgba(76, 133, 240, 0.8) 0%, rgba(96, 96, 246, 0.8) 100%)'
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {trainingProgress}%
                    </Typography>
                  </Box>
                </Box>
                
                <Box mt={2} display="flex" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    Step {trainingStep} of 2
                  </Typography>
                  
                  <Typography variant="caption" color="text.secondary">
                    {trainingStep === 1 ? 'Creating training data...' : 'Fine-tuning model...'}
                  </Typography>
                </Box>
                
                {trainingError && trainingAttempts >= 3 && (
                  <Button 
                    variant="outlined" 
                    color="primary"
                    size="small"
                    onClick={() => {
                      setTrainingAttempts(0);
                      handleTrainFile(trainingFileId, selectedFileName);
                    }}
                    sx={{ mt: 2 }}
                  >
                    Try Again Manually
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Websites Tab */}
        <Box hidden={tabValue !== 1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">Website Sources</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddLinkIcon />}
              onClick={handleWebsiteDialog}
            >
              Add Website
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>URL</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Pages</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Added On</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {websites.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box py={3}>
                        <Typography variant="body1" color="text.secondary">
                          No websites added yet
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  websites.map((website) => (
                    <TableRow key={website.id}>
                      <TableCell>
                        <Box 
                          component="a" 
                          href={website.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          sx={{ 
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {website.url}
                        </Box>
                      </TableCell>
                      <TableCell>{website.title || 'N/A'}</TableCell>
                      <TableCell>{website.pages}</TableCell>
                      <TableCell>{renderStatusChip(website.status)}</TableCell>
                      <TableCell>{new Date(website.scrapedAt).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDeleteWebsite(website.id)}>
                            <DeleteIcon color="error" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Usage & Stats Tab */}
        <Box hidden={tabValue !== 2}>
          <Typography variant="h5" gutterBottom>
            Bot Usage Statistics
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Token Usage
                  </Typography>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="h3" color="primary">
                      {usage.tokensUsed.toLocaleString()}
                    </Typography>
                    <Typography variant="body1" ml={1}>
                      tokens
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Tokens used in conversations with this bot
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Total Requests
                  </Typography>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="h3" color="primary">
                      {usage.requestsMade.toLocaleString()}
                    </Typography>
                    <Typography variant="body1" ml={1}>
                      requests
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    API requests made by this bot
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Display additional usage metrics here */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Suggested Improvements
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Increase Knowledge Base" 
                        secondary="Add more documents to improve the accuracy of bot responses" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Add FAQ Website" 
                        secondary="Include your FAQ page to help the bot answer common questions" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Fine-tune Bot Personality" 
                        secondary="Adjust the personality settings to better match your brand voice" 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Embed Code Tab */}
        <Box hidden={tabValue !== 3}>
          <Typography variant="h5" gutterBottom>
            Embed this Bot
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Embed Code</Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<FileCopyIcon />}
                      onClick={handleCopyEmbed}
                    >
                      Copy Code
                    </Button>
                  </Box>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={embedCode}
                    InputProps={{
                      readOnly: true,
                    }}
                    multiline
                    rows={3}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Deployment Status</Typography>
                    <Button
                      variant="outlined"
                      startIcon={<WebIcon />}
                      onClick={handleDeployUrlDialog}
                    >
                      {embedStatus.deployedUrl ? 'Update URL' : 'Add URL'}
                    </Button>
                  </Box>

                  {embedStatus.deployed ? (
                    <Box>
                      <Chip 
                        icon={<CheckIcon />}
                        label="Deployed"
                        color="success"
                        sx={{ mb: 2 }}
                      />
                      {embedStatus.deployedUrl && (
                        <Box>
                          <Typography variant="body1" gutterBottom>
                            Deployed at:
                          </Typography>
                          <Box 
                            component="a" 
                            href={embedStatus.deployedUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            sx={{
                              color: theme.palette.primary.main,
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {embedStatus.deployedUrl}
                            <ArrowForwardIcon fontSize="small" sx={{ ml: 1 }} />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <Chip
                        icon={<PendingIcon />}
                        label="Not Deployed"
                        color="warning"
                        sx={{ mb: 2 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Add your website URL where you've embedded this bot
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Implementation Steps
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="1. Copy the embed code above" 
                        secondary="Use the copy button to copy the code to your clipboard" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="2. Add the code to your website" 
                        secondary="Paste the code into the <body> section of your HTML" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="3. Save and deploy your changes" 
                        secondary="After deploying, add your website URL to track the deployment" 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    );
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #121212 0%, #2c3e50 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Circuit board pattern background - Same as TestChat */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z' fill='%23ffffff' fill-opacity='0.3' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          zIndex: 0
        }}
      />

      {/* Animated background elements - Same as TestChat */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          zIndex: 0
        }}
      >
        {/* Flowing particles */}
        {[...Array(8)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: ['100px', '150px', '200px', '250px', '300px', '350px'][i],
              height: ['100px', '150px', '200px', '250px', '300px', '350px'][i],
              background: `rgba(76, 133, 240, ${0.03 + (i % 3) * 0.01})`,
              borderRadius: '50%',
              top: `${10 + i * 15}%`,
              left: `${5 + i * 15}%`,
              animation: `float ${5 + i * 2}s infinite ease-in-out alternate`,
              opacity: 0.4,
            }}
          />
        ))

        /* Top-right gradient blob */}
        <Box
          sx={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '500px',
            height: '500px',
            background: `radial-gradient(circle, ${theme.palette.primary.main}22, transparent 70%)`,
            opacity: 0.4,
            filter: 'blur(80px)',
            zIndex: 0,
            animation: 'pulse 15s infinite alternate'
          }}
        />
        
        {/* Mini robot animations */}
        <MiniRobot position={{ top: '15%', left: '85%' }} />
        <MiniRobot position={{ top: '25%', left: '10%' }} />
        <MiniRobot position={{ bottom: '15%', right: '5%' }} />
      </Box>

      {/* App Bar - Enhanced with blur effect */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'rgba(30, 35, 45, 0.85)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={toggleDrawer}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box 
            component="img" 
            src="/robot1.svg"
            alt="GorillaForge AI"
            sx={{ 
              height: 40, 
              mr: 1.5,
              display: { xs: 'none', md: 'block' },
              animation: 'float 3s infinite ease-in-out' 
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNTYgMjU2Ij48cmVjdCB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgZmlsbD0ibm9uZSIvPjxjaXJjbGUgY3g9IjEyOCIgY3k9IjEyOCIgcj0iNzAiIGZpbGw9IiM0Qzg1RjAiLz48cmVjdCB4PSI4OCIgeT0iODgiIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcng9IjE2IiBmaWxsPSIjMjEyMTMxIi8+PHJlY3QgeD0iMTA0IiB5PSIxMDQiIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgcng9IjgiIGZpbGw9IiM0Qzg1RjAiLz48L3N2Zz4=';
            }}
          />
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1 }}
          >
            GorillaForge AI - My Bots
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton color="inherit">
              <NotificationsIcon />
            </IconButton>
            <IconButton
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                }}
              >
                {user?.name?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
            <Button
              component={Link}
              to="/bots/create"
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              sx={{ 
                ml: 2, 
                display: { xs: 'none', sm: 'flex' },
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: '0 4px 10px rgba(76, 133, 240, 0.3)',
              }}
            >
              New Bot
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Profile menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
          }
        }}
      >
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Drawer (mobile) */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            width: drawerWidth,
            background: 'rgba(30, 35, 45, 0.95)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            borderRight: '1px solid rgba(255,255,255,0.08)'
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Main navigation first */}
          <List>
            <ListItem button component={Link} to="/">
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                <DashboardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItem>
            <ListItem button selected>
              <ListItemIcon sx={{ color: theme.palette.primary.main, minWidth: 40 }}>
                <SmartToyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="My Bots" />
            </ListItem>
            <ListItem button component={Link} to="/support">
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                <HeadsetIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Customer Support" />
            </ListItem>
            <ListItem button>
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                <StorageIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Conversations" />
            </ListItem>
            <ListItem button>
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
          </List>
          
          <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)', my: 1 }} />
          
          {/* Bot list now comes after the main navigation */}
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="rgba(255,255,255,0.7)" gutterBottom>
              MY BOTS LIST
            </Typography>
          </Box>
          
          <List sx={{ px: 1, overflow: 'auto', flexGrow: 1 }}>
            {bots.map((bot) => (
              <ListItem
                key={bot.id}
                button
                selected={selectedBot && selectedBot.id === bot.id}
                onClick={() => handleSelectBot(bot)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(76, 133, 240, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(76, 133, 240, 0.3)',
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                  <SmartToyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={bot.name}
                  primaryTypographyProps={{
                    noWrap: true,
                    fontSize: '0.9rem'
                  }}
                />
              </ListItem>
            ))}
          </List>
          
          <Box sx={{ p: 2, mt: 'auto' }}>
            <Button
              component={Link}
              to="/bots/create"
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              fullWidth
              sx={{ 
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                mb: 1
              }}
            >
              New Bot
            </Button>
            <Button 
              variant="outlined" 
              color="inherit" 
              onClick={handleLogout}
              fullWidth
              sx={{ 
                borderColor: 'rgba(255,255,255,0.3)', 
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.05)' }
              }}
            >
              Logout
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Drawer (desktop) - Reorganized with My Bots list below the main navigation */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { 
            width: drawerWidth,
            background: 'rgba(30, 35, 45, 0.95)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            borderRight: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            flexDirection: 'column'
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Main navigation first */}
          <List>
            <ListItem button component={Link} to="/">
              <ListItemIcon sx={{ color: 'white' }}>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItem>
            <ListItem button selected sx={{
              backgroundColor: 'rgba(76, 133, 240, 0.15) !important',
              borderRight: `3px solid ${theme.palette.primary.main}`
            }}>
              <ListItemIcon sx={{ color: theme.palette.primary.main }}>
                <SmartToyIcon />
              </ListItemIcon>
              <ListItemText primary="My Bots" primaryTypographyProps={{ fontWeight: 'medium' }} />
            </ListItem>
            <ListItem button component={Link} to="/support">
              <ListItemIcon sx={{ color: 'white' }}>
                <HeadsetIcon />
              </ListItemIcon>
              <ListItemText primary="Customer Support" />
            </ListItem>
            <ListItem button>
              <ListItemIcon sx={{ color: 'white' }}>
                <StorageIcon />
              </ListItemIcon>
              <ListItemText primary="Conversations" />
            </ListItem>
            <ListItem button>
              <ListItemIcon sx={{ color: 'white' }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
          </List>
          
          <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)', my: 1 }} />
          
          {/* Bot list section header with styled label */}
          <Box sx={{ 
            px: 3, 
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                color: 'rgba(255,255,255,0.7)',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontSize: '0.75rem'
              }}
            >
              My Bots
            </Typography>
            <Chip 
              label={bots.length} 
              size="small" 
              sx={{ 
                height: 20, 
                fontSize: '0.7rem',
                bgcolor: 'rgba(76, 133, 240, 0.2)',
                color: theme.palette.primary.main,
                fontWeight: 'bold' 
              }} 
            />
          </Box>
          
          {/* Bot list now comes after main navigation */}
          <Box sx={{ 
            flexGrow: 1, 
            overflowY: 'auto',
            px: 1,
            // Styled scrollbar for webkit browsers
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
          }}>
            {bots.length === 0 ? (
              <Box sx={{ 
                p: 2, 
                textAlign: 'center', 
                color: 'rgba(255,255,255,0.5)',
                border: '1px dashed rgba(255,255,255,0.1)',
                borderRadius: 1,
                mx: 1,
                my: 2
              }}>
                <SmartToyIcon sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
                <Typography variant="caption" display="block">
                  No bots yet
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {bots.map((bot) => (
                  <ListItem
                    key={bot.id}
                    button
                    selected={selectedBot && selectedBot.id === bot.id}
                    onClick={() => handleSelectBot(bot)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      py: 1,
                      transition: 'all 0.2s ease',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(76, 133, 240, 0.15)',
                        '&:hover': {
                          backgroundColor: 'rgba(76, 133, 240, 0.25)',
                        }
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: '0.9rem',
                        mr: 1.5,
                        backgroundColor: bot.customizations?.primaryColor || theme.palette.primary.main,
                        boxShadow: selectedBot && selectedBot.id === bot.id ? 
                          `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}` : 'none'
                      }}
                    >
                      {bot.name.charAt(0)}
                    </Avatar>
                    <ListItemText
                      primary={bot.name}
                      secondary={bot.description?.substring(0, 25) + (bot.description?.length > 25 ? '...' : '')}
                      primaryTypographyProps={{
                        noWrap: true,
                        fontWeight: selectedBot && selectedBot.id === bot.id ? 'medium' : 'normal',
                        color: 'white'
                      }}
                      secondaryTypographyProps={{
                        noWrap: true,
                        fontSize: '0.75rem',
                        color: 'rgba(255, 255, 255, 0.5)'
                      }}
                    />
                    {selectedBot && selectedBot.id === bot.id && (
                      <Box 
                        sx={{ 
                          width: 4, 
                          height: 4, 
                          borderRadius: '50%', 
                          bgcolor: theme.palette.primary.main,
                          position: 'absolute',
                          right: 8,
                          animation: 'pulse 1.5s infinite'
                        }} 
                      />
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
          
          {/* Create bot and logout buttons at the bottom */}
          <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <Button
              component={Link}
              to="/bots/create"
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              sx={{
                mb: 1.5,
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                py: 1,
                '&:hover': {
                  boxShadow: '0 6px 15px rgba(0,0,0,0.3)',
                  transform: 'translateY(-2px)'
                },
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
            >
              Create New Bot
            </Button>
            
            <Button
              onClick={handleLogout}
              variant="outlined"
              color="inherit"
              fullWidth
              startIcon={<LogoutIcon />}
              size="small"
              sx={{ 
                borderColor: 'rgba(255,255,255,0.2)', 
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { 
                  borderColor: 'white', 
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.05)' 
                }
              }}
            >
              Logout
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Main content - Enhanced content styling */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: { xs: 2, md: 3 }, 
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          zIndex: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
      >
        <Toolbar /> {/* Add Toolbar to create space at the top for the fixed AppBar */}
        
        {loading ? (
          // Enhanced loading state with animation
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            flexGrow={1}
            sx={{ position: 'relative' }}
          >
            <Box
              sx={{
                position: 'relative',
                width: 100,
                height: 100,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  border: '3px solid rgba(255, 255, 255, 0.1)',
                  borderTopColor: theme.palette.primary.main,
                  animation: 'spin 1s linear infinite',
                }}
              />
              <CircularProgress size={60} sx={{ color: 'white', opacity: 0.8 }} />
              
              <Box
                sx={{
                  position: 'absolute',
                  animation: 'pulse 2s infinite ease-in-out'
                }}
              >
                <SmartToyIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
            </Box>
          </Box>
        ) : error ? (
          // Enhanced error state with glass morphism
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            flexGrow={1}
          >
            <Paper 
              elevation={0} 
              sx={{ 
                maxWidth: 600, 
                backdropFilter: 'blur(10px)',
                background: 'rgba(244, 67, 54, 0.15)',
                border: '1px solid rgba(244, 67, 54, 0.3)',
                borderRadius: 2,
                p: 3,
                color: 'white'
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: '#f44336' }}>
                Something went wrong
              </Typography>
              <Typography variant="body2" paragraph sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {error}
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<RefreshIcon />} 
                onClick={handleRefresh} 
                color="error"
              >
                Try Again
              </Button>
            </Paper>
          </Box>
        ) : bots.length === 0 ? (
          // Empty state with enhanced styling
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            sx={{
              flexGrow: 1,
              border: '2px dashed rgba(255,255,255,0.2)',
              borderRadius: 3,
              p: 4,
              color: 'white',
              m: 2,
              backdropFilter: 'blur(10px)',
              background: 'rgba(30, 35, 45, 0.3)',
            }}
          >
            <SmartToyIcon sx={{ fontSize: 80, mb: 3, opacity: 0.7, animation: 'pulse 2s infinite ease-in-out' }} />
            <Typography variant="h5" component="h1" mb={2} textAlign="center" fontWeight="medium">
              You haven't created any bots yet
            </Typography>
            <Typography variant="body1" color="rgba(255,255,255,0.7)" textAlign="center" mb={4} maxWidth="600px">
              Create your first AI assistant to start enhancing your website or application
            </Typography>
            <Button
              component={Link}
              to="/bots/create"
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                py: 1.2,
                px: 4,
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                borderRadius: '30px',
                '&:hover': {
                  boxShadow: '0 6px 25px rgba(0,0,0,0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
            >
              Create Your First Bot
            </Button>
          </Box>
        ) : !selectedBot ? (
          // No bot selected state with enhanced styling
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            flexGrow={1}
            sx={{
              backdropFilter: 'blur(10px)',
              background: 'rgba(30, 35, 45, 0.3)',
              borderRadius: 3,
              p: 4,
              m: 2,
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <Box 
              sx={{ 
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: 'rgba(76, 133, 240, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                border: '1px solid rgba(76, 133, 240, 0.3)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: -5,
                  left: -5,
                  right: -5,
                  bottom: -5,
                  borderRadius: '50%',
                  border: '1px dashed rgba(76, 133, 240, 0.5)',
                  animation: 'spin 20s linear infinite'
                }
              }}
            >
              <SmartToyIcon sx={{ fontSize: 60, color: theme.palette.primary.main, opacity: 0.8 }} />
            </Box>
            <Typography variant="h5" color="white" gutterBottom fontWeight="medium">
              Select a bot from the sidebar
            </Typography>
            <Typography variant="body1" color="rgba(255,255,255,0.7)" mb={3} textAlign="center">
              Choose one of your existing bots to manage, or create a new one
            </Typography>
            <Button
              component={Link}
              to="/bots/create"
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                py: 1.2,
                px: 3,
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
              }}
            >
              Create New Bot
            </Button>
          </Box>
        ) : (
          // Main content for selected bot - with glass morphism styling
          <Box sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            animation: 'fadeIn 0.5s ease-out'
          }}>
            {/* Bot Header */}
            <Box 
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 3,
                p: 2,
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                background: 'rgba(30, 35, 45, 0.7)',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                animation: 'fadeInUp 0.5s ease-out'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 0 } }}>
                <Avatar 
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    backgroundColor: selectedBot.customizations?.primaryColor || theme.palette.primary.main,
                    mr: 2,
                    boxShadow: `0 0 0 4px rgba(255,255,255,0.1)`
                  }}
                >
                  {selectedBot.avatar ? (
                    <img 
                      src={selectedBot.avatar} 
                      alt={selectedBot.name} 
                      style={{ width: '100%', height: '100%' }} 
                    />
                  ) : (
                    <Typography variant="h4" sx={{ color: 'white' }}>
                      {selectedBot.name.charAt(0)}
                    </Typography>
                  )}
                </Avatar>
                <Box>
                  <Typography variant="h5" component="h1" color="white" fontWeight="500" gutterBottom={false}>
                    {selectedBot.name}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        backgroundColor: selectedBot.status === 'inactive' ? 'orange' : '#66BB6A',
                        mr: 1,
                        animation: 'pulse 1.5s infinite'
                      }} 
                    />
                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                      {selectedBot.status === 'inactive' ? 'Inactive' : 'Active'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Box display="flex" gap={2} flex="1" justifyContent="flex-end" flexWrap="wrap">
                <Button
                  component={Link}
                  to={`/bots/${selectedBot.id}/test`}
                  variant="contained"
                  startIcon={<WebIcon />}
                  sx={{
                    background: `linear-gradient(90deg, ${selectedBot.customizations?.primaryColor || theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.3)'
                    }
                  }}
                >
                  Test Website
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  sx={{ 
                    color: 'white', 
                    borderColor: 'rgba(255,255,255,0.5)',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
            
            {/* Split layout with Summary Card and Tabs */}
            <Grid container spacing={2} sx={{ animation: 'fadeInUp 0.5s ease-out 0.2s both' }}>
              {/* Left column: Summary Card with Shortcut Buttons */}
              <Grid item xs={12} md={4} lg={3}>
                <Paper
                  sx={{ 
                    borderRadius: 3,
                    backdropFilter: 'blur(10px)',
                    background: 'rgba(30, 35, 45, 0.7)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    mb: { xs: 2, md: 0 }
                  }}
                >
                  {/* Bot Stats Summary */}
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ 
                      color: 'white', 
                      mb: 2, 
                      position: 'relative',
                      pb: 1,
                      '&:after': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        bottom: 0,
                        width: '40px',
                        height: '3px',
                        background: selectedBot.customizations?.primaryColor || theme.palette.primary.main,
                        borderRadius: '2px'
                      }
                    }}>
                      Bot Summary
                    </Typography>

                    {/* Stats Grid */}
                    <Grid container spacing={1} sx={{ mb: 3 }}>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                          p: 2,
                          borderRadius: 2,
                          textAlign: 'center',
                          transition: 'transform 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            backgroundColor: 'rgba(255, 255, 255, 0.08)'
                          }
                        }}>
                          <StorageIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }} />
                          <Typography variant="h5" sx={{ color: 'white', fontWeight: 'medium' }}>
                            {knowledgeFiles.length}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Files
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                          p: 2,
                          borderRadius: 2,
                          textAlign: 'center',
                          transition: 'transform 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            backgroundColor: 'rgba(255, 255, 255, 0.08)'
                          }
                        }}>
                          <LanguageIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }} />
                          <Typography variant="h5" sx={{ color: 'white', fontWeight: 'medium' }}>
                            {websites.length}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            Websites
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Usage Statistics */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 1.5
                      }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Tokens Used
                        </Typography>
                        <Chip 
                          label={usage.tokensUsed.toLocaleString()} 
                          size="small" 
                          sx={{ 
                            backgroundColor: 'rgba(76, 133, 240, 0.15)', 
                            color: 'white',
                            borderRadius: '4px'
                          }}
                        />
                      </Box>

                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 1.5
                      }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Requests Made
                        </Typography>
                        <Chip 
                          label={usage.requestsMade.toLocaleString()} 
                          size="small" 
                          sx={{ 
                            backgroundColor: 'rgba(76, 133, 240, 0.15)', 
                            color: 'white',
                            borderRadius: '4px'
                          }}
                        />
                      </Box>

                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center'
                      }}>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          Status
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box 
                            sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              backgroundColor: selectedBot.status === 'inactive' ? '#FFC107' : '#66BB6A',
                              mr: 1,
                              animation: selectedBot.status !== 'inactive' ? 'pulse 1.5s infinite' : 'none'
                            }} 
                          />
                          <Typography variant="body2" sx={{ color: 'white' }}>
                            {selectedBot.status === 'inactive' ? 'Inactive' : 'Active'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Shortcut Buttons */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Button
                        component={Link}
                        to={`/bots/${selectedBot.id}/test`}
                        variant="contained"
                        fullWidth
                        startIcon={<WebIcon />}
                        sx={{ 
                          background: `linear-gradient(90deg, ${selectedBot.customizations?.primaryColor || theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                          py: 1.2,
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(0,0,0,0.3)'
                          }
                        }}
                      >
                        Test Website
                      </Button>

                      <Button
                        onClick={() => navigate(`/bots/${selectedBot.id}/edit`)}
                        variant="outlined"
                        fullWidth
                        startIcon={<EditIcon />}
                        sx={{ 
                          color: 'white', 
                          borderColor: 'rgba(255,255,255,0.3)',
                          '&:hover': {
                            borderColor: 'white',
                            backgroundColor: 'rgba(255,255,255,0.05)'
                          }
                        }}
                      >
                        Edit Bot
                      </Button>

                      <Button
                        onClick={() => handleTrainBot(selectedBot.id)}
                        variant="outlined"
                        fullWidth
                        disabled={trainingBot || (knowledgeFiles.length === 0 && websites.length === 0)}
                        startIcon={trainingBot ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                        sx={{ 
                          borderColor: 'rgba(255,255,255,0.3)',
                          color: 'white',
                          '&:hover': {
                            borderColor: 'white',
                            backgroundColor: 'rgba(255,255,255,0.05)'
                          },
                          '&.Mui-disabled': {
                            color: 'rgba(255,255,255,0.3)',
                            borderColor: 'rgba(255,255,255,0.1)'
                          }
                        }}
                      >
                        {trainingBot ? 'Training...' : 'Train Bot'}
                      </Button>
                    </Box>
                  </Box>
                  
                  {/* Embed Status Section */}
                  <Box sx={{ 
                    p: 3,
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    mt: 'auto'
                  }}>
                    <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
                      Embed Status
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Box 
                        sx={{ 
                          width: 8, 
                          height: 8, 
                          borderRadius: '50%', 
                          backgroundColor: embedStatus.deployed ? '#66BB6A' : '#FFC107',
                          mr: 1,
                          animation: embedStatus.deployed ? 'pulse 1.5s infinite' : 'none'
                        }} 
                      />
                      <Typography variant="body2" sx={{ color: 'white', flex: 1 }}>
                        {embedStatus.deployed ? 'Deployed' : 'Not Deployed'}
                      </Typography>
                      
                      <Tooltip title={embedStatus.deployed ? 'View Deploy URL' : 'Set Deploy URL'}>
                        <IconButton 
                          size="small" 
                          onClick={handleDeployUrlDialog}
                          sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    {embedStatus.deployedUrl && (
                      <Box 
                        component="a" 
                        href={embedStatus.deployedUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        sx={{
                          color: theme.palette.primary.main,
                          textDecoration: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '0.8rem',
                          backgroundColor: 'rgba(0,0,0,0.2)',
                          p: 1,
                          borderRadius: 1,
                          border: '1px solid rgba(255,255,255,0.05)',
                          '&:hover': { 
                            textDecoration: 'underline',
                            backgroundColor: 'rgba(0,0,0,0.3)'
                          }
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'rgba(255,255,255,0.8)', 
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: 1
                          }}
                        >
                          {embedStatus.deployedUrl}
                        </Typography>
                        <ArrowForwardIcon fontSize="small" sx={{ ml: 1, color: 'rgba(255,255,255,0.5)' }} />
                      </Box>
                    )}
                  </Box>
                  
                  {/* Add Public Link section */}
                  <Box 
                    sx={{ 
                      mt: 2, 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 1,
                      transition: 'background-color 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.08)'
                      }
                    }}
                  >
                    <Typography variant="caption" color="rgba(255, 255, 255, 0.7)" sx={{ display: 'flex', alignItems: 'center' }}>
                      <ShareIcon fontSize="small" sx={{ mr: 0.5, fontSize: '1rem' }} />
                      {selectedBot.isPublic ? 'Public Link' : 'Private Link'}
                      {selectedBot.isPublic && (
                        <Box 
                          component="span" 
                          sx={{ 
                            ml: 1, 
                            px: 0.8, 
                            py: 0.2, 
                            bgcolor: 'success.main', 
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '0.65rem',
                            fontWeight: 'bold'
                          }}
                        >
                          PUBLIC
                        </Box>
                      )}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={selectedBotId === selectedBot._id && copySuccess ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                      onClick={() => handleCopyPublicLink(selectedBot._id || selectedBot.id)}
                      sx={{ 
                        color: selectedBotId === selectedBot._id && copySuccess ? 'success.main' : 'rgba(255, 255, 255, 0.7)', 
                        fontSize: '0.75rem',
                        py: 0.5,
                        minWidth: 'auto',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      {selectedBotId === selectedBot._id && copySuccess ? 'Copied!' : 'Copy'}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
              
              {/* Right column: Tabs Box */}
              <Grid item xs={12} md={8} lg={9}>
                {/* Tabs and Content - with glass morphism and animations */}
                <Paper 
                  sx={{ 
                    borderRadius: 3,
                    overflow: 'hidden',
                    backdropFilter: 'blur(10px)',
                    background: 'rgba(30, 35, 45, 0.7)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '600px',
                  }}
                >
                  <Tabs
                    value={tabValue}
                    onChange={handleChangeTab}
                    variant="fullWidth"
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{ 
                      borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                      '& .MuiTab-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        py: 2,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '0%',
                          height: '3px',
                          backgroundColor: theme.palette.primary.main,
                          transition: 'width 0.3s ease'
                        },
                        '&:hover': {
                          color: 'white',
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                          '&::after': {
                            width: '30%'
                          }
                        },
                        '&.Mui-selected': {
                          color: 'white',
                          '&::after': {
                            width: '70%'
                          }
                        }
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    <Tab icon={<ArticleIcon />} label="Knowledge Base" />
                    <Tab icon={<WebIcon />} label="Websites" />
                    <Tab icon={<StorageIcon />} label="Usage" />
                    <Tab icon={<CodeIcon />} label="Embed" />
                  </Tabs>
                  
                  {/* Tab Panels with enhanced height and styling */}
                  <Box sx={{ 
                    p: 3, 
                    flexGrow: 1, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    overflowY: 'auto',
                    color: 'white',
                    // Styled scrollbar
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'transparent',
                    },
                  }}>
                    {/* Knowledge Files Tab */}
                    <TabPanel value={tabValue} index={0} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* ... existing knowledge files tab content ... */}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h6">Knowledge Files</Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<UploadIcon />}
                          onClick={handleUploadDialog}
                          sx={{
                            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                          }}
                        >
                          Upload File
                        </Button>
                      </Box>
                      
                      {/* ... rest of knowledge files tab content ... */}
                    </TabPanel>
                    
                    {/* Websites Tab */}
                    <TabPanel value={tabValue} index={1} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* ... existing websites tab content ... */}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Typography variant="h6">Website Sources</Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<AddLinkIcon />}
                          onClick={handleWebsiteDialog}
                          sx={{
                            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                          }}
                        >
                          Add Website
                        </Button>
                      </Box>
                      
                      {/* ... rest of websites tab content ... */}
                    </TabPanel>
                    
                    {/* Usage Tab */}
                    <TabPanel value={tabValue} index={2} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* ... existing usage tab content ... */}
                      <Typography variant="h6" gutterBottom>
                        Bot Usage Statistics
                      </Typography>
                      
                      {/* ... rest of usage tab content ... */}
                    </TabPanel>
                    
                    {/* Embed Tab */}
                    <TabPanel value={tabValue} index={3} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      {/* ... existing embed tab content ... */}
                      <Typography variant="h6" gutterBottom>
                        Embed this Bot
                      </Typography>
                      
                      {/* ... rest of embed tab content ... */}
                    </TabPanel>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
      
      {/* Snackbar for notifications - Enhanced with glass morphism */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            backdropFilter: 'blur(10px)',
            background: snackbar.severity === 'success' 
              ? 'rgba(76, 175, 80, 0.9)' 
              : snackbar.severity === 'error'
                ? 'rgba(244, 67, 54, 0.9)'
                : 'rgba(33, 150, 243, 0.9)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'white',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ... existing dialogs and modals ... */}
      
      {/* CSS for animations */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes float {
          0% { transform: translateY(0) rotate(0deg); }
          100% { transform: translateY(-20px) rotate(10deg); }
        }
        
        @keyframes glowBorder {
          0%, 100% { border-color: rgba(76, 133, 240, 0.3); }
          50% { border-color: rgba(76, 133, 240, 0.8); }
        }
        
        @keyframes pulseRing {
          0% { transform: scale(1); opacity: 0.6; }
          70% { opacity: 0; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        
        /* ...existing animations... */
      `}</style>
    </Box>
  );
};

// TabPanel component (fixed version)
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bot-tabpanel-${index}`}
      aria-labelledby={`bot-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

const generateEmbedCode = (botId) => {
  const scriptSrc = `https://gorillaforge-ai-server.onrender.com/embed/chatbot.js`;
  return {
    wordpress: `<div id="chatbox-container"></div>
<script src="${scriptSrc}" data-bot-id="${botId}" defer></script>`,
    express: `
app.use('/chatbot', express.static('path/to/chatbot.js'));
app.get('/chatbot', (req, res) => {
  res.send('<div id="chatbox-container"></div><script src="${scriptSrc}" data-bot-id="${botId}" defer></script>');
});
    `,
    node: `
const http = require('http');
const fs = require('fs');

http.createServer((req, res) => {
  if (req.url === '/chatbot') {
    res.writeHead(200, { 'Content-Type': 'text/html' });
    res.end('<div id="chatbox-container"></div><script src="${scriptSrc}" data-bot-id="${botId}" defer></script>');
  } else {
    res.writeHead(404);
    res.end();
  }
}).listen(3000, () => console.log('Server running on port 3000'));
    `,
  };
};

export default MyBots;
